import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  IconButton,
  Divider,
  Switch,
  FormControlLabel,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import StarIcon from "@mui/icons-material/Star";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import PsychologyIcon from "@mui/icons-material/Psychology";
import DevicesIcon from "@mui/icons-material/Devices";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import SpeedIcon from "@mui/icons-material/Speed";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";

const PremiumUpsellDialog = ({ open, onClose, trigger = "general" }) => {
  const [billingCycle, setBillingCycle] = useState("annual"); // 'monthly' or 'annual'

  const pricingPlans = {
    monthly: {
      premium: 19,
      provider: 99,
      enterprise: 499,
    },
    annual: {
      premium: 199, // Save $29 (2 months free)
      provider: 999, // Save $189 (~2 months free)
      enterprise: 4999, // Save $989 (~2 months free)
    },
  };

  const savingsPercentage = {
    premium: Math.round(((12 * 19 - 199) / (12 * 19)) * 100),
    provider: Math.round(((12 * 99 - 999) / (12 * 99)) * 100),
    enterprise: Math.round(((12 * 499 - 4999) / (12 * 499)) * 100),
  };

  const features = {
    free: [
      { text: "100 glossary terms", icon: <CheckCircleIcon /> },
      { text: "Basic search & filter", icon: <CheckCircleIcon /> },
      { text: "AI voice pronunciations", icon: <CheckCircleIcon /> },
      { text: "10 bookmarks (local only)", icon: <CheckCircleIcon /> },
    ],
    premium: [
      { text: "All Free features", icon: <CheckCircleIcon />, bold: true },
      {
        text: "Full 200+ glossary terms",
        icon: <VolumeUpIcon />,
        highlight: true,
      },
      {
        text: "Professional audio recordings",
        icon: <StarIcon />,
        highlight: true,
      },
      { text: "Unlimited bookmarks (synced)", icon: <BookmarkIcon /> },
      { text: "Download audio files (MP3)", icon: <CloudDownloadIcon /> },
      {
        text: "AI personalized glossary",
        icon: <PsychologyIcon />,
        highlight: true,
      },
      { text: "Offline access", icon: <DevicesIcon /> },
      { text: "No ads", icon: <LockOpenIcon /> },
      { text: "Priority support", icon: <SupportAgentIcon /> },
    ],
    provider: [
      { text: "All Premium features", icon: <CheckCircleIcon />, bold: true },
      { text: "Patient management tools", icon: <TrendingUpIcon /> },
      { text: "Batch trial matching", icon: <SpeedIcon /> },
      { text: "White-label reports", icon: <CheckCircleIcon /> },
      { text: "Analytics dashboard", icon: <TrendingUpIcon /> },
      { text: "Clinical calculators", icon: <CheckCircleIcon /> },
      { text: "API access", icon: <CheckCircleIcon /> },
      { text: "Custom integrations", icon: <CheckCircleIcon /> },
      { text: "Dedicated support", icon: <SupportAgentIcon /> },
    ],
    enterprise: [
      { text: "All Provider features", icon: <CheckCircleIcon />, bold: true },
      { text: "Multi-user accounts", icon: <CheckCircleIcon /> },
      { text: "Custom EMR integrations", icon: <CheckCircleIcon /> },
      { text: "On-premise deployment option", icon: <CheckCircleIcon /> },
      { text: "Training sessions", icon: <CheckCircleIcon /> },
      {
        text: "Priority feature requests",
        icon: <StarIcon />,
        highlight: true,
      },
      { text: "Usage analytics", icon: <TrendingUpIcon /> },
      { text: "SLA guarantee", icon: <CheckCircleIcon /> },
      {
        text: "Dedicated account manager",
        icon: <SupportAgentIcon />,
        highlight: true,
      },
    ],
  };

  const getTriggerMessage = () => {
    switch (trigger) {
      case "audio_limit":
        return {
          title: "🎧 Unlock Unlimited Audio",
          subtitle:
            "You've reached your free audio limit (10/day). Upgrade for unlimited professional pronunciations!",
        };
      case "bookmark_limit":
        return {
          title: "🔖 Save More Terms",
          subtitle:
            "Free plan allows 10 bookmarks. Upgrade for unlimited bookmarks synced across devices!",
        };
      case "term_locked":
        return {
          title: "🔓 Unlock Premium Content",
          subtitle:
            "This term is available in Premium. Get access to 100+ additional medical terms!",
        };
      case "download":
        return {
          title: "📥 Download Audio Files",
          subtitle:
            "Download professional audio recordings for offline use with Premium!",
        };
      default:
        return {
          title: "🚀 Upgrade to Premium",
          subtitle:
            "Get the most out of NephroTrax with unlimited features!",
        };
    }
  };

  const message = getTriggerMessage();

  const handleUpgrade = (plan) => {
    console.log(`Upgrading to ${plan} - ${billingCycle}`);
    // TODO: Integrate with Stripe or payment processor
    // For now, redirect to pricing/checkout page
    window.location.href = `/pricing?plan=${plan}&billing=${billingCycle}`;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          background:
            "linear-gradient(135deg, rgba(102, 126, 234, 0.02) 0%, rgba(118, 75, 162, 0.02) 100%)",
        },
      }}
    >
      {/* Header */}
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              {message.title}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {message.subtitle}
            </Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ color: "text.secondary" }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Billing Toggle */}
        <Box sx={{ textAlign: "center", mb: 4 }}>
          <FormControlLabel
            control={
              <Switch
                checked={billingCycle === "annual"}
                onChange={(e) =>
                  setBillingCycle(e.target.checked ? "annual" : "monthly")
                }
                color="primary"
              />
            }
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography
                  variant="body1"
                  fontWeight={billingCycle === "monthly" ? "bold" : "normal"}
                >
                  Monthly
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  |
                </Typography>
                <Typography
                  variant="body1"
                  fontWeight={billingCycle === "annual" ? "bold" : "normal"}
                >
                  Annual
                </Typography>
                {billingCycle === "annual" && (
                  <Chip
                    label="Save up to 17%"
                    size="small"
                    color="success"
                    sx={{ fontWeight: "bold", ml: 1 }}
                  />
                )}
              </Box>
            }
          />
        </Box>

        {/* Pricing Cards */}
        <Grid container spacing={3}>
          {/* Free Plan */}
          <Grid item xs={12} md={3}>
            <Card
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                border: "2px solid",
                borderColor: "grey.300",
                position: "relative",
              }}
            >
              <CardContent sx={{ flexGrow: 1, p: 3 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Free
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h3" fontWeight="bold">
                    $0
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    forever
                  </Typography>
                </Box>

                <Divider sx={{ my: 2 }} />

                <List dense>
                  {features.free.map((feature, idx) => (
                    <ListItem key={idx} sx={{ px: 0 }}>
                      <ListItemIcon
                        sx={{ minWidth: 36, color: "success.main" }}
                      >
                        {feature.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={feature.text}
                        primaryTypographyProps={{
                          fontSize: "0.9rem",
                          fontWeight: feature.bold ? 600 : 400,
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>

              <Box sx={{ p: 2 }}>
                <Button variant="outlined" fullWidth disabled>
                  Current Plan
                </Button>
              </Box>
            </Card>
          </Grid>

          {/* Premium Plan - FEATURED */}
          <Grid item xs={12} md={3}>
            <Card
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                border: "3px solid",
                borderColor: "primary.main",
                position: "relative",
                transform: "scale(1.05)",
                boxShadow: 6,
              }}
            >
              {/* Most Popular Badge */}
              <Box
                sx={{
                  position: "absolute",
                  top: -15,
                  left: "50%",
                  transform: "translateX(-50%)",
                  bgcolor: "primary.main",
                  color: "white",
                  px: 3,
                  py: 0.5,
                  borderRadius: 2,
                  fontWeight: "bold",
                  fontSize: "0.85rem",
                  boxShadow: 2,
                }}
              >
                ⭐ MOST POPULAR
              </Box>

              <CardContent sx={{ flexGrow: 1, p: 3, pt: 4 }}>
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  gutterBottom
                  color="primary.main"
                >
                  Premium
                </Typography>
                <Box sx={{ mb: 1 }}>
                  <Typography
                    variant="h3"
                    fontWeight="bold"
                    color="primary.main"
                  >
                    ${pricingPlans[billingCycle].premium}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {billingCycle === "monthly" ? "per month" : "per year"}
                  </Typography>
                  {billingCycle === "annual" && (
                    <Typography
                      variant="caption"
                      color="success.main"
                      fontWeight="bold"
                    >
                      Save ${12 * 19 - 199} ({savingsPercentage.premium}% off)
                    </Typography>
                  )}
                </Box>

                <Divider sx={{ my: 2 }} />

                <List dense>
                  {features.premium.map((feature, idx) => (
                    <ListItem key={idx} sx={{ px: 0 }}>
                      <ListItemIcon
                        sx={{
                          minWidth: 36,
                          color: feature.highlight
                            ? "primary.main"
                            : "success.main",
                        }}
                      >
                        {feature.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={feature.text}
                        primaryTypographyProps={{
                          fontSize: "0.9rem",
                          fontWeight:
                            feature.bold || feature.highlight ? 600 : 400,
                          color: feature.highlight ? "primary.main" : "inherit",
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>

              <Box sx={{ p: 2 }}>
                <Button
                  variant="contained"
                  fullWidth
                  size="large"
                  onClick={() => handleUpgrade("premium")}
                  sx={{
                    background:
                      "linear-gradient(135deg, #1A5EA8 0%, #0B2545 100%)",
                    fontWeight: "bold",
                    py: 1.5,
                  }}
                >
                  Upgrade Now
                </Button>
              </Box>
            </Card>
          </Grid>

          {/* Provider Plan */}
          <Grid item xs={12} md={3}>
            <Card
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                border: "2px solid",
                borderColor: "secondary.main",
                position: "relative",
              }}
            >
              <CardContent sx={{ flexGrow: 1, p: 3 }}>
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  gutterBottom
                  color="secondary.main"
                >
                  Provider
                </Typography>
                <Box sx={{ mb: 1 }}>
                  <Typography
                    variant="h3"
                    fontWeight="bold"
                    color="secondary.main"
                  >
                    ${pricingPlans[billingCycle].provider}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {billingCycle === "monthly" ? "per month" : "per year"}
                  </Typography>
                  {billingCycle === "annual" && (
                    <Typography
                      variant="caption"
                      color="success.main"
                      fontWeight="bold"
                    >
                      Save ${12 * 99 - 999} ({savingsPercentage.provider}% off)
                    </Typography>
                  )}
                </Box>

                <Divider sx={{ my: 2 }} />

                <List dense>
                  {features.provider.slice(0, 9).map((feature, idx) => (
                    <ListItem key={idx} sx={{ px: 0 }}>
                      <ListItemIcon
                        sx={{ minWidth: 36, color: "secondary.main" }}
                      >
                        {feature.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={feature.text}
                        primaryTypographyProps={{
                          fontSize: "0.9rem",
                          fontWeight: feature.bold ? 600 : 400,
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>

              <Box sx={{ p: 2 }}>
                <Button
                  variant="contained"
                  fullWidth
                  color="secondary"
                  onClick={() => handleUpgrade("provider")}
                  sx={{ fontWeight: "bold", py: 1.5 }}
                >
                  Upgrade to Provider
                </Button>
              </Box>
            </Card>
          </Grid>

          {/* Enterprise Plan */}
          <Grid item xs={12} md={3}>
            <Card
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                border: "2px solid",
                borderColor: "warning.main",
                position: "relative",
              }}
            >
              <CardContent sx={{ flexGrow: 1, p: 3 }}>
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  gutterBottom
                  color="warning.main"
                >
                  Enterprise
                </Typography>
                <Box sx={{ mb: 1 }}>
                  <Typography
                    variant="h3"
                    fontWeight="bold"
                    color="warning.main"
                  >
                    ${pricingPlans[billingCycle].enterprise}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {billingCycle === "monthly" ? "per month" : "per year"}
                  </Typography>
                  {billingCycle === "annual" && (
                    <Typography
                      variant="caption"
                      color="success.main"
                      fontWeight="bold"
                    >
                      Save ${12 * 499 - 4999} ({savingsPercentage.enterprise}%
                      off)
                    </Typography>
                  )}
                </Box>

                <Divider sx={{ my: 2 }} />

                <List dense>
                  {features.enterprise.slice(0, 9).map((feature, idx) => (
                    <ListItem key={idx} sx={{ px: 0 }}>
                      <ListItemIcon
                        sx={{
                          minWidth: 36,
                          color: feature.highlight
                            ? "warning.main"
                            : "success.main",
                        }}
                      >
                        {feature.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={feature.text}
                        primaryTypographyProps={{
                          fontSize: "0.9rem",
                          fontWeight:
                            feature.bold || feature.highlight ? 600 : 400,
                          color: feature.highlight ? "warning.main" : "inherit",
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>

              <Box sx={{ p: 2 }}>
                <Button
                  variant="contained"
                  fullWidth
                  color="warning"
                  onClick={() => handleUpgrade("enterprise")}
                  sx={{ fontWeight: "bold", py: 1.5 }}
                >
                  Contact Sales
                </Button>
              </Box>
            </Card>
          </Grid>
        </Grid>

        {/* Trust Signals */}
        <Box sx={{ mt: 4, textAlign: "center" }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            ✅ 30-day money-back guarantee • 🔒 Cancel anytime • 💳 Secure
            payment
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Join 1,000+ healthcare professionals using NephroTrax
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} color="inherit">
          Maybe Later
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PremiumUpsellDialog;
